import { browserSupportsWebAuthn } from "@simplewebauthn/browser";
import { useState } from "react";
import type { Avatar } from "../../shared/types";
import { useShell } from "../app/shell";
import {
  useLogout,
  useSaveAvatar,
  useSaveProfile,
  useSubmitting,
} from "../data/mutations";
import { useAccount } from "../data/session";
import { t } from "../i18n";
import { FormError, Modal } from "../ui/components";
import { notify } from "../ui/toast";
import { AvatarPicker } from "./avatar-picker";
import { ProfileEditor } from "./profile-editor";

export function EditProfileDialog() {
  const { close } = useShell();
  const me = useAccount()?.me;
  const profile = useSaveProfile();
  const submitting = useSubmitting();
  const [error, setError] = useState("");
  async function save(changes: {
    name: string;
    username: string;
    bio: string;
  }) {
    setError("");
    try {
      await profile.mutateAsync(changes);
      close();
      notify(t("تم حفظ التغييرات"), "success");
    } catch (e) {
      setError((e as Error).message);
    }
  }
  return (
    <Modal title={t("تعديل الحساب")} onClose={close}>
      {me && (
        <ProfileEditor
          person={me}
          disabled={submitting}
          error={error}
          onSave={(changes) => void save(changes)}
        />
      )}
    </Modal>
  );
}

export function AvatarDialog() {
  const { close } = useShell();
  const me = useAccount()?.me;
  const saveAvatar = useSaveAvatar();
  const submitting = useSubmitting();
  const [error, setError] = useState("");
  async function save(avatar: Avatar | null) {
    setError("");
    try {
      await saveAvatar.mutateAsync(avatar);
      close();
      notify(t("تم حفظ التغييرات"), "success");
    } catch (e) {
      setError((e as Error).message);
    }
  }
  return (
    <Modal title={t("صورة الحساب")} onClose={close}>
      {me && (
        <AvatarPicker
          person={me}
          disabled={submitting}
          error={error}
          onSave={(avatar) => void save(avatar)}
        />
      )}
    </Modal>
  );
}

export function useSignOut() {
  const { session, close } = useShell();
  const logout = useLogout();
  return async () => {
    await session.end(false);
    await logout.mutateAsync();
    close();
    notify(t("تم تسجيل الخروج"), "success");
  };
}

export function LogoutDialog() {
  const { close, recovery } = useShell();
  const signOut = useSignOut();
  const submitting = useSubmitting();
  const [error, setError] = useState("");
  async function leave() {
    try {
      await signOut();
    } catch (e) {
      setError((e as Error).message);
    }
  }
  return (
    <Modal title={t("تسجيل الخروج دون طريقة للعودة؟")} onClose={close}>
      <h2>{t("تسجيل الخروج دون طريقة للعودة؟")}</h2>
      <p className="modal-description">
        {t(
          "بدون مفتاح مرور أو رمز استعادة لن تتمكن من العودة إلى هذا الحساب بعد تسجيل الخروج.",
        )}
      </p>
      <FormError message={error} />
      <div className="confirm-actions">
        <button
          className="button secondary"
          disabled={submitting}
          onClick={() => {
            close();
            void recovery.protect();
          }}
        >
          {t(
            browserSupportsWebAuthn() ? "إضافة مفتاح مرور" : "حفظ رمز الاستعادة",
          )}
        </button>
        <button
          className="button danger-button"
          disabled={submitting}
          onClick={() => void leave()}
        >
          {t("تسجيل الخروج على أي حال")}
        </button>
      </div>
    </Modal>
  );
}
